import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Map of the scanner screens' zones (Leaflet + OpenStreetMap, no key). The edited screen's pin
// is draggable and its circle follows; a click on the map moves the pin too. Other screens are
// drawn grey for context. While no screen has a zone, the default warehouse point from .env is
// drawn dashed: that is what phones are checked against until a zone exists.
const pinIcon = L.divIcon({ className: 'att-pin', iconSize: [18, 18], iconAnchor: [9, 9] });
const OSM = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

const AttendanceZoneMap = ({ screens, selectedId, site, point, radiusM, onMove }) => {
  const el = useRef(null), map = useRef(null), others = useRef(null), pin = useRef(null), ring = useRef(null);
  const move = useRef(onMove); move.current = onMove;

  useEffect(() => {
    const m = L.map(el.current);
    // OpenStreetMap answers 403 to tile requests without a Referer, so never let a page policy strip it
    L.tileLayer(OSM, { maxZoom: 19, attribution: '&copy; OpenStreetMap', referrerPolicy: 'strict-origin-when-cross-origin' }).addTo(m);
    others.current = L.layerGroup().addTo(m);
    m.on('click', e => move.current({ lat: e.latlng.lat, lng: e.latlng.lng }));
    map.current = m;
    const c = point || site;
    m.setView([c.lat, c.lng], point ? 16 : 15);
    return () => { m.remove(); map.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const g = others.current; g.clearLayers();
    for (const s of screens) {
      if (s.zone && s.id !== selectedId) L.circle([s.zone.lat, s.zone.lng], { radius: s.zone.radiusM, color: '#64748B', weight: 1.5, fillOpacity: 0.08 }).bindTooltip(s.label).addTo(g);
    }
    if (!screens.some(s => s.zone && s.status === 'bound') && site) {
      L.circle([site.lat, site.lng], { radius: site.radiusM, color: '#94A3B8', weight: 1.5, dashArray: '5 5', fillOpacity: 0.04 }).bindTooltip('Default warehouse point (.env)').addTo(g);
    }
  }, [screens, selectedId, site]);

  // The pin is created once the zone has a point and then only moved, so dragging never re-centres the map.
  useEffect(() => {
    const m = map.current;
    if (!point) { if (pin.current) { pin.current.remove(); ring.current.remove(); pin.current = ring.current = null; } return; }
    if (!pin.current) {
      ring.current = L.circle([point.lat, point.lng], { radius: radiusM, color: '#2e9e4f', weight: 2, fillOpacity: 0.12 }).addTo(m);
      pin.current = L.marker([point.lat, point.lng], { draggable: true, icon: pinIcon }).addTo(m);
      pin.current.on('drag', () => ring.current.setLatLng(pin.current.getLatLng()));
      pin.current.on('dragend', () => { const p = pin.current.getLatLng(); move.current({ lat: p.lat, lng: p.lng }); });
      m.setView([point.lat, point.lng], Math.max(m.getZoom(), 16));
    } else {
      pin.current.setLatLng([point.lat, point.lng]); ring.current.setLatLng([point.lat, point.lng]);
    }
  }, [point, radiusM]);
  useEffect(() => { if (ring.current) ring.current.setRadius(radiusM); }, [radiusM]);

  return <div ref={el} className="att-map" />;
};

export default AttendanceZoneMap;
